import { HttpClient, HttpEventType, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { easyFormatter } from '../easyFormatter';
import { Observable, from, of } from 'rxjs';
import { ArrangementDocument } from '../models/arrangement';
import _ from 'lodash';
import { combineAll, finalize, map, switchMap } from 'rxjs/operators';
import { FileModel } from '../models/file';
import { SiteService } from './site.service';

const STALL_TIMEOUT_MS = 60 * 1000; // 60 seconds of no data transfer = stalled
export const MAX_FILE_SIZE_BYTES = 500 * 1000 * 1000; // 500 MB
export const MAX_FILE_SIZE_LABEL = '500MB';

export interface UploadProgress {
  fileName: string;
  loaded: number;
  total: number;
  percent: number;
}

interface UploadData {
  fileName: string;
  fileType: string;
}

export interface UploadDataWithoutPresignedUrl extends UploadData {
  fileContent: string;
}

export interface UploadDataWithPresignedUrl extends UploadData {
  fileRawContent: File;
}

@Injectable({
  providedIn: 'root',
})
export class FileService {
  constructor(
    private siteService: SiteService,
    private http: HttpClient
  ) {}

  // For < 6 Mb files
  // Keep this for simplicity of avatar cropper library
  //
  // `acl: 'public-read'` is optional. When passed (e.g. for email-signature
  // composites/frames that must survive longer than a presigned URL) the server
  // uploads the object public-read with a stable, non-expiring URL. Omitted →
  // the object stays private (the default for avatars, documents, etc.).
  uploadFilesWithoutPreSignedUrl(body: {
    prefix: string;
    uploadData: UploadDataWithoutPresignedUrl[];
    acl?: 'public-read';
  }) {
    const url = easyFormatter(environment.api.host + environment.api.paths.api.files.post, {});

    return this.http.post(url, body) as Observable<{ files: FileModel[] }>;
  }

  // For unlimited file size
  uploadFilesWithPreSignedUrl(
    body: { prefix: string; uploadData: UploadDataWithPresignedUrl[] },
    onProgress?: (progress: UploadProgress) => void
  ) {
    return this.transferWithPreSignedUrl<{ files: FileModel[] }>(
      {
        preSignedApiUrl:
          environment.api.host + environment.api.paths.api.files.generatePresignedUploadUrl,
        saveApiUrl: environment.api.host + environment.api.paths.api.files.saveUploadedFiles,
        presignExtras: { prefix: body.prefix },
        saveExtras: {},
        uploadData: body.uploadData,
      },
      onProgress
    );
  }

  /**
   * presign -> PUT-to-S3-with-progress -> save, parameterised by the two API URLs
   * and the extra fields each of them takes. Shared with LibraryService, which
   * presigns and saves against folder-scoped endpoints but transfers identically —
   * including the stall timer and the Content-Type that must match the signature.
   */
  transferWithPreSignedUrl<T>(
    options: {
      preSignedApiUrl: string;
      saveApiUrl: string;
      presignExtras: Record<string, any>;
      saveExtras: Record<string, any>;
      uploadData: UploadDataWithPresignedUrl[];
    },
    onProgress?: (progress: UploadProgress) => void
  ) {
    const { preSignedApiUrl, saveApiUrl, presignExtras, saveExtras, uploadData } = options;

    this.siteService.addSubscriptionLog(
      this,
      'file.service.ts->transferWithPreSignedUrl->from(uploadData)'
    );

    return from(uploadData).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised(
          'file.service.ts->transferWithPreSignedUrl->from(uploadData)'
        )
      ),
      map((uploadItem) => {
        // step 1 - generate presigned urls
        return this.http
          .post<{ preSignedUrl: string; path: string; contentType?: string }>(preSignedApiUrl, {
            ...presignExtras,
            fileName: uploadItem.fileName,
          })
          .pipe(
            // step 2 - do upload with progress tracking and stall detection
            switchMap(({ preSignedUrl, path, contentType }) => {
              // HttpRequest is immutable — the Content-Type MUST be set via the
              // constructor options. The previous `req.headers.set(...)` was a
              // no-op that silently dropped the header, so the PUT went up with
              // the browser's blob type; when that didn't match the type the
              // server signed the URL with, S3 rejected the PUT (403). Use the
              // exact content-type the server returned so they always match.
              const req = new HttpRequest('PUT', preSignedUrl, uploadItem.fileRawContent, {
                reportProgress: true,
                headers: new HttpHeaders({ 'Content-Type': contentType || uploadItem.fileType }),
              });

              return new Observable<{ preSignedUrl: string; path: string }>((observer) => {
                let stallTimer: any = null;

                const resetStallTimer = () => {
                  if (stallTimer) {
                    clearTimeout(stallTimer);
                  }
                  stallTimer = setTimeout(() => {
                    sub.unsubscribe();
                    observer.error(
                      new Error(
                        `Upload stalled for "${uploadItem.fileName}" — no data transferred for 60 seconds. Please try again.`
                      )
                    );
                  }, STALL_TIMEOUT_MS);
                };

                resetStallTimer();

                const sub = this.http.request(req).subscribe({
                  next: (event) => {
                    if (event.type === HttpEventType.UploadProgress) {
                      resetStallTimer();
                      if (onProgress) {
                        onProgress({
                          fileName: uploadItem.fileName,
                          loaded: event.loaded || 0,
                          total: event.total || uploadItem.fileRawContent.size,
                          percent: event.total
                            ? Math.round((100 * (event.loaded || 0)) / event.total)
                            : 0,
                        });
                      }
                    } else if (event.type === HttpEventType.Response) {
                      if (stallTimer) {
                        clearTimeout(stallTimer);
                      }
                      observer.next({ preSignedUrl, path });
                      observer.complete();
                    }
                  },
                  error: (err) => {
                    if (stallTimer) {
                      clearTimeout(stallTimer);
                    }
                    observer.error(
                      new Error(
                        `Upload failed for "${uploadItem.fileName}": ${err.message || 'Unknown error'}`
                      )
                    );
                  },
                });

                return () => {
                  if (stallTimer) {
                    clearTimeout(stallTimer);
                  }
                  sub.unsubscribe();
                };
              });
            }),
            switchMap(({ preSignedUrl, path }) =>
              of({
                preSignedUrl,
                path,
                ...uploadItem,
              })
            )
          );
      }),
      combineAll(),
      // step 3 - save file records
      switchMap((uploaded) => {
        return this.http.post<T>(saveApiUrl, {
          ...saveExtras,
          uploadData: uploaded.map((uploadItem) => ({
            fileName: uploadItem.fileName,
            path: uploadItem.path,
          })),
        });
      })
    );
  }

  renameFile(document: ArrangementDocument) {
    const name = _.get(document, '_file.name', '');
    const fileId = _.get(document, 'fileId', '');

    const url = easyFormatter(environment.api.host + environment.api.paths.api.files.rename, {
      fileId,
    });

    this.siteService.addSubscriptionLog(
      this,
      'file.service.ts->renameFile->this.http.post(url, { name, fileId })'
    );

    return this.http.post(url, { name, fileId }).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised(
          'file.service.ts->renameFile->this.http.post(url, { name, fileId })'
        )
      ),
      map((res: any) => res.data.file)
    );
  }

  // SECURITY (C2): the viewFile() method that hit the unauthenticated
  // /files/view-file/:id endpoint was dead code (no callers) and relied on the
  // now-removed JWT bypass. File access uses getPreviewLink/getDownloadLink,
  // which are JWT-protected and return signed S3 URLs.

  getPreviewLink(fileId: string): Observable<string> {
    const url = easyFormatter(
      environment.api.host + environment.api.paths.api.files.getPreviewLink,
      { fileId }
    );

    this.siteService.addSubscriptionLog(
      this,
      'file.service.ts->getPreviewLink->this.http.get(url)'
    );

    return this.http.get(url).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised(
          'file.service.ts->getPreviewLink->this.http.get(url)'
        )
      ),
      map((res: any) => res.data.url)
    );
  }

  getDownloadLink(fileId: string): Observable<string> {
    const url = easyFormatter(
      environment.api.host + environment.api.paths.api.files.getDownloadLink,
      { fileId }
    );

    this.siteService.addSubscriptionLog(
      this,
      'file.service.ts->getDownloadLink->this.http.get(url)'
    );

    return this.http.get(url).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised(
          'file.service.ts->getDownloadLink->this.http.get(url)'
        )
      ),
      map((res: any) => res.data.url)
    );
  }
}
